import React, { useState } from "react";
import { useSavings } from "../../context/SavingsContext";
import ReceiptRow from "./ReceiptRow";
import { SavingEntry } from "../../types";

const WallOfReceipts: React.FC = () => {
  const { data } = useSavings();
  const [filter, setFilter] = useState<"all" | "recurring" | "one-time">("all");
  const [sortField, setSortField] = useState<keyof SavingEntry>("date");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  const filteredEntries = data.entries.filter((entry) => {
    if (filter === "all") return true;
    return entry.type === filter;
  });

  const sortedEntries = [...filteredEntries].sort((a, b) => {
    // Create variables with explicit type assertions to avoid "possibly undefined" errors
    const aValue = a[sortField] as any;
    const bValue = b[sortField] as any;

    // Now compare the values
    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const handleSort = (field: keyof SavingEntry) => {
    if (field === sortField) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const totalSavings = sortedEntries.reduce((sum, entry) => {
    if (entry.type === "recurring") {
      return sum + entry.amount * 12; // Annual projection for recurring
    }
    return sum + entry.amount;
  }, 0);

  const formatCurrency = (amount: number): string => {
    return amount.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    });
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="bg-gray-900 p-4 md:p-6 rounded-lg">
      <div className="flex flex-col md:flex-row justify-between items-center mb-6">
        <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-0">Wall of Receipts</h2>
        <div className="flex space-x-2 md:space-x-4">
          <button
            className={`px-3 md:px-4 py-2 rounded text-sm md:text-base ${
              filter === "all" ? "bg-blue-600" : "bg-gray-700"
            }`}
            onClick={() => setFilter("all")}
          >
            All
          </button>
          <button
            className={`px-3 md:px-4 py-2 rounded text-sm md:text-base ${
              filter === "recurring" ? "bg-blue-600" : "bg-gray-700"
            }`}
            onClick={() => setFilter("recurring")}
          >
            Recurring
          </button>
          <button
            className={`px-3 md:px-4 py-2 rounded text-sm md:text-base ${
              filter === "one-time" ? "bg-blue-600" : "bg-gray-700"
            }`}
            onClick={() => setFilter("one-time")}
          >
            One-Time
          </button>
        </div>
      </div>

      <div className="mb-4 text-sm md:text-base">
        <p>
          Displaying {sortedEntries.length} entries totaling approximately{" "}
          {formatCurrency(totalSavings)} in savings.
        </p>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4">
        {sortedEntries.map((entry) => (
          <div key={entry.id} className="bg-gray-800 rounded-lg p-4">
            <div className="flex justify-between items-start mb-2">
              <div>
                <span className="text-xs text-gray-400">{entry.department}</span>
                {entry.vendor && (
                  <span className="text-xs text-gray-400"> • {entry.vendor}</span>
                )}
              </div>
              <span
                className={`px-2 py-1 rounded text-xs ${
                  entry.type === "recurring"
                    ? "bg-green-900 text-green-300"
                    : "bg-blue-900 text-blue-300"
                }`}
              >
                {entry.type === "recurring" ? "Recurring" : "One-Time"}
              </span>
            </div>
            <p className="text-sm mb-3">{entry.description}</p>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-400">{formatDate(entry.date)}</span>
              <span
                className={`text-lg font-bold ${
                  entry.type === "recurring" ? "text-green-400" : "text-blue-400"
                }`}
              >
                {formatCurrency(entry.amount)}
                {entry.type === "recurring" && (
                  <span className="text-xs text-gray-400 ml-1">/mo</span>
                )}
              </span>
            </div>
          </div>
        ))}
        {sortedEntries.length === 0 && (
          <div className="bg-gray-800 rounded-lg p-4 text-center text-gray-400">
            No entries found. Add some savings to see them listed here.
          </div>
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full bg-gray-800 rounded-lg">
          <thead>
            <tr className="bg-gray-700 text-left">
              <th
                className="px-6 py-3 cursor-pointer"
                onClick={() => handleSort("department")}
              >
                Department
                {sortField === "department" &&
                  (sortDirection === "asc" ? " ↑" : " ↓")}
              </th>
              <th
                className="px-6 py-3 cursor-pointer"
                onClick={() => handleSort("vendor")}
              >
                Vendor
                {sortField === "vendor" &&
                  (sortDirection === "asc" ? " ↑" : " ↓")}
              </th>
              <th
                className="px-6 py-3 cursor-pointer"
                onClick={() => handleSort("description")}
              >
                Description
                {sortField === "description" &&
                  (sortDirection === "asc" ? " ↑" : " ↓")}
              </th>
              <th
                className="px-6 py-3 cursor-pointer"
                onClick={() => handleSort("date")}
              >
                Date
                {sortField === "date" &&
                  (sortDirection === "asc" ? " ↑" : " ↓")}
              </th>
              <th
                className="px-6 py-3 text-right cursor-pointer"
                onClick={() => handleSort("amount")}
              >
                Amount
                {sortField === "amount" &&
                  (sortDirection === "asc" ? " ↑" : " ↓")}
              </th>
              <th
                className="px-6 py-3 text-center cursor-pointer"
                onClick={() => handleSort("type")}
              >
                Type
                {sortField === "type" &&
                  (sortDirection === "asc" ? " ↑" : " ↓")}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedEntries.map((entry) => (
              <ReceiptRow key={entry.id} entry={entry} />
            ))}
            {sortedEntries.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-4 text-center text-gray-400">
                  No entries found. Add some savings to see them listed here.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WallOfReceipts;
